/**
 * CalendarSettings: the calendar the AI books meetings into (2026-09-18).
 *
 * When a lead is ready for a call, the AI reads the connected calendar,
 * offers two real open times, asks for the lead's email once they pick one,
 * books the meeting directly (the calendar sends the invite) and confirms in
 * the LinkedIn thread. Replaces the old "Calendly coming soon" placeholder.
 */

import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { CalendarCheck, Loader2 } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { PlanGate } from '@/components/plan-gate'
import { useApi } from '@/hooks/use-api'
import { usePlan } from '@/hooks/use-plan'
import { ProviderCard, integrationKeys, type IntegrationRow } from './integrations-settings'

const GOOGLE_RESULT: Record<string, { text: string; ok: boolean }> = {
  connected: { text: 'Google Calendar connected. The AI can now book meetings into it.', ok: true },
  cancelled: { text: 'Google sign-in was cancelled.', ok: false },
  expired: { text: 'The Google sign-in link expired. Try Connect with Google again.', ok: false },
  failed: { text: 'Google did not return access. Try again and allow calendar access.', ok: false },
  missing_scope: { text: 'Calendar access was not granted. Try again and tick the calendar permissions.', ok: false },
  not_configured: { text: 'Google Calendar is not configured on this environment yet.', ok: false },
}

export default function CalendarSettings() {
  const apiFetch = useApi()
  const plan = usePlan()
  const [searchParams] = useSearchParams()
  const googleResult = GOOGLE_RESULT[searchParams.get('google') ?? '']

  const { data, isLoading } = useQuery({
    queryKey: integrationKeys.list,
    queryFn: async (): Promise<IntegrationRow[]> => {
      const res = await apiFetch<IntegrationRow[]>('/api/settings/integrations')
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to load integrations')
      return res.data
    },
    staleTime: 60_000,
  })
  const rows = (data ?? []).filter((r) => r.category === 'calendar')

  return (
    <PlanGate requiredPlan="pro" currentPlan={plan} replace>
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <CalendarCheck className="h-5 w-5 text-cobalt-bright" />
            <CardTitle>Calendar & Booking</CardTitle>
          </div>
          <CardDescription>
            When a lead is ready for a call, the AI checks your calendar, offers two open times,
            asks for their email once they pick one, books the meeting and confirms in the chat.
            The invite comes from your calendar. Connect one calendar below.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {googleResult && (
            <p className={googleResult.ok ? 'text-sm text-emerald-500' : 'text-sm text-destructive'}>{googleResult.text}</p>
          )}
          {isLoading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading calendars
            </div>
          )}
          {rows.map((row) => (
            <ProviderCard key={row.provider} row={row} />
          ))}
          <p className="text-xs text-muted-foreground">
            Without a connected calendar, a campaign whose link is a GoHighLevel booking page still gets two real
            open times offered, and the lead books through the link. Times are shown in the campaign's timezone.
          </p>
        </CardContent>
      </Card>
    </PlanGate>
  )
}
